import { useCallback, useEffect, useRef } from 'react';

export interface UseScannerBridgeOptions {
  autoStart?: boolean;
  elementId?: string;
  onScan?: (code: string, meta?: { symbology?: string }) => void;
}

type ScannerModule = typeof import('../components/scanner');

export function useScannerBridge({ autoStart = true, onScan }: UseScannerBridgeOptions = {}) {
  const handlerRef = useRef(onScan);
  handlerRef.current = onScan;

  const moduleRef = useRef<ScannerModule | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);

  const detach = useCallback(() => {
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }
  }, []);

  const attachListener = useCallback(async () => {
    const module = moduleRef.current ?? await import('../components/scanner');
    moduleRef.current = module;
    if (!cleanupRef.current) {
      const listener = (payload: { data: string; symbology: string }) => {
        handlerRef.current?.(payload.data, { symbology: payload.symbology });
      };
      module.addScanListener(listener);
      cleanupRef.current = () => module.removeScanListener(listener);
    }
    return module;
  }, []);

  const startScanner = useCallback(async () => {
    try {
      const module = await attachListener();
      await module.ensureScanner();
    } catch (error) {
      detach();
      console.error('[ScannerBridge] Failed to start scanner', error);
      throw error;
    }
  }, [attachListener, detach]);

  const stopScanner = useCallback(async () => {
    try {
      const module = moduleRef.current ?? await import('../components/scanner');
      if (module.stopScanner) {
        await module.stopScanner();
      }
    } catch (error) {
      console.error('[ScannerBridge] Failed to stop scanner', error);
      // If stop fails, try to reset the scanner
      try {
        const module = moduleRef.current ?? await import('../components/scanner');
        if (module.resetScanner) {
          await module.resetScanner();
        }
      } catch (resetError) {
        console.error('[ScannerBridge] Failed to reset scanner', resetError);
      }
    } finally {
      detach();
    }
  }, [detach]);

  const resetScanner = useCallback(async () => {
    try {
      const module = moduleRef.current ?? await import('../components/scanner');
      if (module.resetScanner) {
        await module.resetScanner();
      }
      detach();
    } catch (error) {
      console.error('[ScannerBridge] Failed to reset scanner', error);
    }
  }, [detach]);

  useEffect(() => {
    if (autoStart) {
      startScanner().catch(err => {
        console.error('[ScannerBridge] Auto-start failed', err);
      });
    }
    return () => {
      detach();
    };
  }, [autoStart, detach, startScanner]);

  return {
    startScanner,
    stopScanner,
    resetScanner,
    continueScanning: () => window.continueScanning?.() ?? Promise.resolve(),
  };
}
